import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { getPlaylists, addTrackToPlaylist, createPlaylist } from '../api';

const LAST_PLAYLIST_KEY = 'musicapp_last_playlists';

function getLastPlaylists() {
  try { return JSON.parse(localStorage.getItem(LAST_PLAYLIST_KEY)) || []; } catch { return []; }
}

function saveLastPlaylists(playlist, current) {
  try {
    const updated = [playlist, ...current.filter(p => p.id !== playlist.id)].slice(0, 3);
    localStorage.setItem(LAST_PLAYLIST_KEY, JSON.stringify(updated));
  } catch {}
}

// position = { x, y }  → compact desktop popup anchored near the button
// position = undefined  → mobile bottom sheet
export default function PlaylistPicker({ track, onClose, position }) {
  const isPopup = !!position;
  const ref = useRef(null);
  const [lastPlaylists, setLastPlaylists] = useState(getLastPlaylists);

  const { data } = useQuery(
    ['playlists-context'],
    () => getPlaylists({ page_size: 1000 }).then(r => r.data.results || r.data || []),
    { staleTime: 60000 }
  );
  const playlists = (data || []).filter(p => !p.is_smart);

  useEffect(() => {
    const keyHandler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', keyHandler);
    if (isPopup) {
      const clickHandler = (e) => {
        if (ref.current && !ref.current.contains(e.target)) onClose();
      };
      document.addEventListener('mousedown', clickHandler);
      return () => {
        document.removeEventListener('keydown', keyHandler);
        document.removeEventListener('mousedown', clickHandler);
      };
    }
    return () => document.removeEventListener('keydown', keyHandler);
  }, [onClose, isPopup]);

  const handleAdd = async (playlist) => {
    try {
      await addTrackToPlaylist(playlist.id, track.id);
      saveLastPlaylists(playlist, lastPlaylists);
      setLastPlaylists([playlist, ...lastPlaylists.filter(p => p.id !== playlist.id)].slice(0, 3));
    } catch (e) {
      console.error('Failed to add track to playlist', e);
      alert('Failed to add track to playlist. Please try again.');
    }
    onClose();
  };

  const handleNewPlaylist = async () => {
    const name = window.prompt('New playlist name:');
    if (!name?.trim()) return;
    try {
      const res = await createPlaylist({ name: name.trim(), is_smart: false });
      await handleAdd(res.data);
    } catch (e) {
      alert('Failed to create playlist.');
    }
  };

  const otherPlaylists = playlists.filter(p => !lastPlaylists.find(lp => lp.id === p.id));

  // ── Desktop compact popup ──────────────────────────────────────────────
  if (isPopup) {
    const popupWidth = 240;
    const popupMaxHeight = 400;
    const adjustedX = Math.min(position.x, window.innerWidth - popupWidth - 8);
    const adjustedY = position.y + popupMaxHeight > window.innerHeight
      ? position.y - popupMaxHeight
      : position.y;

    const item = (extra = {}) => ({
      padding: '8px 14px', fontSize: 13, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 8,
      ...extra,
    });

    return (
      <div ref={ref} style={{
        position: 'fixed', left: adjustedX, top: adjustedY, zIndex: 9999,
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, minWidth: popupWidth, maxHeight: popupMaxHeight,
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          padding: '8px 14px', fontSize: 11, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          color: 'var(--text-muted)', borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}>
          Add to Playlist
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          <div
            onClick={handleNewPlaylist}
            style={item({ color: 'var(--accent)', fontWeight: 600 })}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            ＋ New Playlist
          </div>
          <div style={{ borderBottom: '1px solid var(--border)', margin: '2px 0' }} />

          {lastPlaylists.length > 0 && (
            <>
              {lastPlaylists.map(lp => (
                <div key={lp.id} onClick={() => handleAdd(lp)}
                  style={item({ color: 'var(--accent)' })}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ fontSize: 10 }}>★</span> {lp.name}
                </div>
              ))}
              <div style={{ borderBottom: '1px solid var(--border)', margin: '2px 0' }} />
            </>
          )}

          {otherPlaylists.map(p => (
            <div key={p.id} onClick={() => handleAdd(p)}
              style={item({ color: 'var(--text)' })}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {p.name}
            </div>
          ))}

          {playlists.length === 0 && (
            <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-muted)' }}>
              No playlists found
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Mobile bottom sheet ───────────────────────────────────────────────
  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.5)' }}
      />
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10001,
        background: 'var(--bg2)', borderRadius: '16px 16px 0 0',
        maxHeight: '70vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 -8px 32px rgba(0,0,0,0.5)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 0' }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.2)' }} />
        </div>
        <div style={{
          padding: '10px 20px 12px', borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 2 }}>
              Add to Playlist
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 'min(280px, 70vw)' }}>
              {track.title}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}>
            ✕
          </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          <div
            onClick={handleNewPlaylist}
            style={{ padding: '14px 20px', fontSize: 14, cursor: 'pointer', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600 }}
          >
            ＋ New Playlist
          </div>
          <div style={{ borderBottom: '1px solid var(--border)' }} />

          {lastPlaylists.length > 0 && (
            <>
              {lastPlaylists.map(lp => (
                <div key={lp.id} onClick={() => handleAdd(lp)}
                  style={{ padding: '14px 20px', fontSize: 14, cursor: 'pointer', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 10 }}
                >
                  <span style={{ fontSize: 10 }}>★</span> {lp.name}
                </div>
              ))}
              <div style={{ borderBottom: '1px solid var(--border)' }} />
            </>
          )}

          {otherPlaylists.map(p => (
            <div key={p.id} onClick={() => handleAdd(p)}
              style={{ padding: '14px 20px', fontSize: 14, cursor: 'pointer', color: 'var(--text)' }}
            >
              {p.name}
            </div>
          ))}

          {playlists.length === 0 && (
            <div style={{ padding: '24px 20px', fontSize: 14, color: 'var(--text-muted)', textAlign: 'center' }}>
              No playlists yet
            </div>
          )}
        </div>
      </div>
    </>
  );
}
